/**
 * useUserRole — derives the current user's role label and the permission
 * flags gated by it from the Vuex auth user's `roles` array.
 *
 * Returns the resolved `role` ('Super Admin' | 'Staff' | 'Observer' | 'User'),
 * `isUserRole`/`isObserver` convenience flags, and boolean permission flags
 * for group privacy (User-role restricted) and group/post/comment/reaction/
 * share/invite actions (Observer-role restricted).
 */
import { computed } from 'vue';
import { useStore } from 'vuex';

/** Returns the current user's role and the permission flags derived from it. */
export function useUserRole() {
  const store = useStore();
  const roles = computed(() => store.getters.getUser?.roles);

  const role = computed(() => {
    const r = roles.value;
    if (!Array.isArray(r)) return 'User';
    if (r.includes('admin')) return 'Super Admin';
    if (r.includes('staff')) return 'Staff';
    if (r.includes('observer')) return 'Observer';
    return 'User';
  });

  const isUserRole = computed(() => role.value === 'User');
  const isObserver = computed(() => role.value === 'Observer');

  // User-role restrictions (group privacy only)
  const canSelectPrivateGroup = computed(() => !isUserRole.value);
  const canManageGroupPrivacy = computed(() => !isUserRole.value);

  // Observer-role restrictions
  const canCreateGroup = computed(() => !isObserver.value);
  const canCreatePost = computed(() => !isObserver.value);
  const canComment = computed(() => !isObserver.value);
  const canReact = computed(() => !isObserver.value);
  const canShare = computed(() => !isObserver.value);
  const canInviteUsers = computed(() => !isObserver.value);

  return {
    role,
    isUserRole,
    isObserver,
    canSelectPrivateGroup,
    canManageGroupPrivacy,
    canCreateGroup,
    canCreatePost,
    canComment,
    canReact,
    canShare,
    canInviteUsers,
  };
}
